import React, { useCallback, useEffect, useState, useRef, useLayoutEffect, } from 'react';
import { View } from 'react-native';
import { Easing, runOnJS, useAnimatedProps, useDerivedValue, useSharedValue, withDelay, withSequence, withTiming, } from 'react-native-reanimated';
import { styles as textStyles } from 'design-system/Text';
import { AppView } from 'design-system/AppView';
import Button, { HEIGHT } from 'design-system/Button';
import { rem } from 'design-system/values';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import { useMarginBottom } from 'hooks/useMarginBottom';
import ReanimatedCircularProgress from 'design-system/ReanimatedCircularProgress';
import { AnimatedTextInput } from 'design-system/AnimatedComponents';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import Hero from 'login/components/Hero';
import ProgressItem from 'login/components/ProgressItem';
import i18n from 'utils/i18n';
import { useAppDispatch, useAppStore } from 'store/hooks';
import { getIncomes } from 'actions';
import { CommonActions } from '@react-navigation/native';
import EmptyComponent from 'design-system/EmptyComponent';
import { useDisableAndroidBackButton } from 'hooks';
import { NavigationHeaderRightTextButton } from 'design-system/NavigationHeaderRightTextButton';
import { isWeb } from '../../../constants';
import { selectPrefilledAverageBudgets } from '../selectors';
import { getBudgets } from '../actions';
const strings = {
    title: `Setting up your budget...`,
    subtitle: 'This will take a few seconds',
    progressItem1: 'Reviewing past spending',
    progressItem2: `${i18n.t('analyzing')} categories`,
    progressItem3: `${i18n.t('finalizing')} your budgets`,
};
export default withAnimatedNavigationHeader(({ navigation, route }) => {
    const { flow, period, resetKey, subtitle, disableBack } = route.params;
    const colors = useColors();
    const store = useAppStore();
    const dispatch = useAppDispatch();
    const handlePress = useCallback(() => {
        if (flow === 'Onboarding') {
            dispatch(getBudgets());
            // SetupBudgetReadyScreen lives in the LoginStack, which
            // hosts this screen during onboarding. navigateDeprecated is typed to
            // the MainStack here, hence the cast.
            const navigate = navigation.navigate;
            navigate('SetupBudgetReadyScreen', { period });
            return;
        }
        navigation.dispatch((state) => {
            const routes = state.routes.filter((r) => ![
                'SelectPeriod',
                'EditBudgetFrequency',
                'SelectDate',
                'HolidaySelection',
                'Rollover',
                'EditBudget',
                'Budgets',
                'SelectPeriod',
                'ProcessAutoSetUpBudget',
            ].includes(r.name));
            return CommonActions.reset({
                ...state,
                index: routes.length - 1,
                routes,
            });
        });
        const budgetState = selectPrefilledAverageBudgets(store.getState());
        const monthlyBudget = budgetState.find((budget) => budget.type === 'overall')?.baseLimit || 0;
        navigation.navigateDeprecated('Budgets', {
            period,
            monthlyBudget,
            budgetState,
            flow,
            hasPrefilledBudgets: true,
        });
    }, [flow, period]);
    const fetchData = useCallback(async () => Promise.all([dispatch(getIncomes()), dispatch(getBudgets())]), []);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerLeft: disableBack ? EmptyComponent : undefined,
            headerBackVisible: !disableBack,
            gestureEnabled: !disableBack,
            headerTintColor: colors.elements.primary,
            headerRight: __DEV__
                ? () => (<NavigationHeaderRightTextButton text="Skip" onPress={async () => {
                        await fetchData();
                        handlePress();
                    }}/>)
                : undefined,
        });
    }, [colors.elements.primary, disableBack, fetchData, handlePress]);
    useDisableAndroidBackButton(useCallback(() => !!disableBack, [disableBack]));
    const styles = useStyles(styleSet);
    const animationSharedValue = useSharedValue(0);
    // --- Web-specific state ---
    const [webProgress, setWebProgress] = useState(0); // 0 to 1
    const webIntervalRef = useRef(null);
    const [renderThirdStep, setRenderThirdStep] = useState(false);
    const [renderFirstStep, setRenderFirstStep] = useState(false);
    const [renderSecondStep, setRenderSecondStep] = useState(false);
    const [renderContinueButton, setRenderContinueButton] = useState(false);
    const [displayText, setDisplayText] = useState('0%');
    useEffect(() => {
        if (renderThirdStep) {
            fetchData();
        }
    }, [period, resetKey, renderThirdStep, fetchData]);
    // --- Web Progress Animation ---
    useEffect(() => {
        if (!isWeb)
            return;
        setWebProgress(0);
        setRenderFirstStep(false);
        setRenderSecondStep(false);
        setRenderThirdStep(false);
        setRenderContinueButton(false);
        let progress = 0;
        const firstBreak = 0.31 + Math.floor(Math.random() * 10) / 100;
        const secondBreak = 0.51 + Math.floor(Math.random() * 10) / 100;
        const thirdBreak = 0.71 + Math.floor(Math.random() * 10) / 100;
        const forthBreak = 0.91 + (Math.floor(Math.random() * 5) + 1) / 100;
        const steps = [firstBreak, secondBreak, thirdBreak, forthBreak, 1];
        const durations = [1000, 2000, 3000, 2000, 2000];
        let currentStep = 0;
        let stepStart = 0;
        let stepEnd = steps[0];
        let stepDuration = durations[0];
        let elapsed = 0;
        const interval = 50;
        if (webIntervalRef.current)
            clearInterval(webIntervalRef.current);
        webIntervalRef.current = setInterval(() => {
            elapsed += interval;
            const t = Math.min(elapsed / stepDuration, 1);
            progress = stepStart + (stepEnd - stepStart) * t;
            setWebProgress(progress);
            setDisplayText(`${Math.floor(progress * 100)}%`);
            // Step triggers
            if (progress > 0.3 && !renderFirstStep)
                setRenderFirstStep(true);
            if (progress > 0.6 && !renderSecondStep)
                setRenderSecondStep(true);
            if (progress > 0.9 && !renderThirdStep)
                setRenderThirdStep(true);
            if (progress >= 1 && !renderContinueButton)
                setRenderContinueButton(true);
            if (t >= 1) {
                currentStep += 1;
                if (currentStep < steps.length) {
                    stepStart = steps[currentStep - 1];
                    stepEnd = steps[currentStep];
                    stepDuration = durations[currentStep];
                    elapsed = 0;
                }
                else {
                    clearInterval(webIntervalRef.current);
                }
            }
        }, interval);
        // eslint-disable-next-line consistent-return
        return () => {
            if (webIntervalRef.current)
                clearInterval(webIntervalRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // --- Native Progress Animation ---
    useEffect(() => {
        if (isWeb)
            return;
        const firstBreak = 0.31 + Math.floor(Math.random() * 10) / 100;
        const secondBreak = 0.51 + Math.floor(Math.random() * 10) / 100;
        const thirdBreak = 0.71 + Math.floor(Math.random() * 10) / 100;
        const forthBreak = 0.91 + (Math.floor(Math.random() * 5) + 1) / 100;
        animationSharedValue.value = 0;
        animationSharedValue.value = withDelay(600, withSequence(withTiming(firstBreak, { duration: 1000 }), withTiming(secondBreak, { duration: 2000 }), withTiming(thirdBreak, { duration: 3000 }), withTiming(forthBreak, { duration: 2000 }), withTiming(1, { duration: 2000, easing: Easing.cubic })));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useDerivedValue(() => {
        if (isWeb)
            return;
        if (animationSharedValue.value === 1) {
            runOnJS(setRenderContinueButton)(true);
        }
        else if (animationSharedValue.value > 0.9) {
            runOnJS(setRenderThirdStep)(true);
        }
        else if (animationSharedValue.value > 0.6) {
            runOnJS(setRenderSecondStep)(true);
        }
        else if (animationSharedValue.value > 0.3) {
            runOnJS(setRenderFirstStep)(true);
        }
    }, [animationSharedValue]);
    const progressText = useDerivedValue(() => `${Math.floor(animationSharedValue.value * 100)}%`, [animationSharedValue]);
    const animatedTextProps = useAnimatedProps(() => ({
        text: progressText.value,
    }), [progressText]);
    // --- Web Progress Circle ---
    const WebCircularProgress = ({ percent, children, }) => {
        const radius = 100;
        const stroke = 12;
        const normalizedRadius = radius - stroke / 2;
        const circumference = 2 * Math.PI * normalizedRadius;
        const strokeDashoffset = circumference * (1 - percent);
        return (<svg width={2 * radius} height={2 * radius} viewBox={`0 0 ${2 * radius} ${2 * radius}`}>
          <circle r={normalizedRadius} cx={radius} cy={radius} stroke={colors.cards.onDark} strokeWidth={stroke} fill="transparent"/>
          <circle r={normalizedRadius} cx={radius} cy={radius} stroke={colors.gradients.ultimate.colors[0]} strokeWidth={stroke} fill="transparent" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} transform={`rotate(-90,${radius},${radius})`}/>
          <foreignObject x={0} y={0} width={2 * radius} height={2 * radius} style={{ pointerEvents: 'none' }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
            }}>
              {children}
            </div>
          </foreignObject>
        </svg>);
    };
    const btnContainerStyles = useMarginBottom('marginBottom', 0, styles.bottomContainer);
    useEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    return (<AppView withFrame>
        <Hero OffBlack style={styles.heroContainer} title={strings.title} subTitle={subtitle || strings.subtitle}/>
        <View style={styles.progressView}>
          {isWeb ? (<WebCircularProgress percent={webProgress}>
              <span style={{
                ...textStyles.Numbers_36,
                color: colors.text.brand,
                textAlign: 'center',
            }}>
                {displayText}
              </span>
            </WebCircularProgress>) : (<ReanimatedCircularProgress progressSharedValue={animationSharedValue}>
              <AnimatedTextInput editable={false} style={styles.progressText} underlineColorAndroid="transparent" animatedProps={animatedTextProps}/>
            </ReanimatedCircularProgress>)}
        </View>
        <View style={styles.flex}>
          {renderFirstStep ? (<ProgressItem title={strings.progressItem1}/>) : null}
          {renderSecondStep ? (<ProgressItem title={strings.progressItem2}/>) : null}
          {renderThirdStep ? (<ProgressItem title={strings.progressItem3}/>) : null}
        </View>
        <View style={btnContainerStyles}>
          {renderContinueButton ? (<Button brand title="Continue" onPress={handlePress}/>) : null}
        </View>
      </AppView>);
}, ({ colors }) => ({
    pageTitle: 'Setting up Budget',
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styleSet = createStyleSheets((colors) => ({
    contentContainer: {
        paddingHorizontal: rem(16),
    },
    heroContainer: {
        marginHorizontal: rem(16),
    },
    flex: {
        flex: 1,
    },
    progressView: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    bottomContainer: {
        height: HEIGHT,
        marginHorizontal: rem(16),
    },
    progressText: {
        ...textStyles.Numbers_36,
        color: colors.text.brand,
        textAlign: 'center',
    },
}));
